import "./new-registration.scss";

import TextBlock from "@shared/ui/text-block/text-block";
import useScreenSize from "@shared/utility/screen-size";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Editor } from "primereact/editor";
import { useState } from "react";
import { FormProvider } from "react-hook-form";
import { FaPhone, FaRegCheckCircle } from "react-icons/fa";
import { MdOutlineEmail } from "react-icons/md";

import { appAsset } from "../../../app.asset";
import { appFeature } from "../../../app.feature";
import FormRegistration from "../form-registration/form-registration";
import { useNewRegistration } from "./new-registration.hook";

export function NewRegistration() {
  const {
    isCreating,
    formMethod,
    tenantData,
    successDialog,
    setSuccessDialog,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  } = useNewRegistration();
  const { isMobileMode } = useScreenSize();
  const [ageVerifyDialog, setAgeVerifyDialog] = useState<boolean>(true);
  const [termsDialog, setTermsDialog] = useState<boolean>(false);
  const [privacyConsentDialog, setPrivacyConsentDialog] =
    useState<boolean>(false);
  const [consent, setConsent] = useState({
    termsAndConditions: false,
    privacyPolicy: false,
  });

  return (
    <div id="NewRegistration" className="registration">
      <Dialog
        header="Age Verification for Rewards Program"
        visible={ageVerifyDialog}
        style={{ width: "50vw" }}
        onHide={() => {
          if (!ageVerifyDialog) return;
          setAgeVerifyDialog(false);
          formMethod.reset();
        }}
        maximized={isMobileMode}
        closable={false}
      >
        <div>
          <p className="mb-5">
            By clicking "Yes, I am 18 or older," you confirm that you meet the
            minimum age requirement to participate in our rewards program and
            agree to the{" "}
            <a
              onClick={() => setTermsDialog(true)}
              className="text-blue-500 hover:underline hover:cursor-pointer"
            >
              Terms and Conditions.
            </a>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Button
              label="No, I am under 18"
              severity="secondary"
              onClick={() => {
                if (tenantData?.allow_under_eighteen) {
                  setAgeVerifyDialog(false);
                } else {
                  navigate("/");
                }
              }}
            />
            <Button
              label="Yes, I am 18 or older"
              onClick={() => {
                setAgeVerifyDialog(false);
              }}
            />
          </div>
        </div>
      </Dialog>
      <Dialog
        header="Registration"
        visible={successDialog}
        style={{ width: "50vw" }}
        onHide={() => {
          if (!successDialog) return;
          setSuccessDialog(false);
          formMethod.reset();
        }}
        maximized={isMobileMode}
      >
        <div className="flex flex-col items-center justify-center">
          {tenantData?.post_registration_message ? (
            <p
              className="my-4"
              dangerouslySetInnerHTML={{
                __html: tenantData.post_registration_message.replace(
                  /\n/g,
                  "<br/>",
                ),
              }}
            ></p>
          ) : (
            <div className="flex flex-row gap-4 items-center justify-center">
              <FaRegCheckCircle className="text-green-600" size={48} />
              <h2 className="text-2xl font-semibold text-gray-800">
                Thank You for registering!
              </h2>
            </div>
          )}

          {tenantData?.post_registration_link ? (
            <Button
              className="block mt-8"
              label="Continue to our site"
              disabled={isCreating}
              onClick={() =>
              (window.location.href =
                tenantData?.post_registration_link ?? "")
              }
            />
          ) : null}
        </div>
      </Dialog>
      <Dialog
        header="Terms and Conditions"
        visible={termsDialog}
        className="w-[80vw]"
        onHide={() => {
          if (!termsDialog) return;
          setTermsDialog(false);
          formMethod.reset();
        }}
        maximized={isMobileMode}
      >
        <div className="flex flex-col items-center justify-center mb-5">
          <Editor
            value={tenantData?.terms_and_conditions}
            readOnly
            showHeader={false}
            className="w-full h-[50vh] overflow-auto"
          />
        </div>
        <div className="flex justify-end">
          <Button
            label="I Agree"
            onClick={() => {
              setTermsDialog(false);
              setConsent({
                ...consent,
                termsAndConditions: true,
              });
            }}
          />
        </div>
      </Dialog>

      <Dialog
        header="Privacy Policy"
        visible={privacyConsentDialog}
        className="w-[80vw]"
        onHide={() => {
          if (!privacyConsentDialog) return;
          setPrivacyConsentDialog(false);
          formMethod.reset();
        }}
        maximized={isMobileMode}
      >
        <div className="flex flex-col items-center justify-center mb-5">
          <Editor
            value={tenantData?.privacy_policy}
            readOnly
            showHeader={false}
            className="w-full h-[50vh] overflow-auto"
          />
        </div>
        <div className="flex justify-end">
          <Button
            label="I Agree"
            onClick={() => {
              setPrivacyConsentDialog(false);
              setConsent({
                ...consent,
                privacyPolicy: true,
              });
            }}
          />
        </div>
      </Dialog>

      <div className="min-h-screen flex flex-col md:flex-row">
        {/* Left Panel (Background Image) */}
        <div
          className="hidden md:block md:relative w-full md:w-1/2 h-48 md:h-auto bg-cover bg-center"
          style={{ backgroundImage: `url('${appAsset.loginBg}')` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <div className="relative z-10 flex flex-col justify-center items-center h-full text-white p-6 md:p-10 text-center">
            <img
              src={appAsset.logo}
              className="w-full md:w-[300px]"
              alt={`${appFeature.company}`}
            />
            {isMobileMode ? null : (
              <>
                <h1 className="text-3xl font-bold mb-2 mt-4">
                  {appFeature.appName}
                </h1>
                <span className="text-center max-w-sm">
                  {tenantData?.email ? (
                    <TextBlock
                      icon={<MdOutlineEmail />}
                      value={tenantData.email}
                      label={"Email"}
                    />
                  ) : null}
                  {tenantData?.phone_number ? (
                    <TextBlock
                      icon={<FaPhone />}
                      value={tenantData.phone_number}
                      label={"Email"}
                    />
                  ) : null}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Right Panel (Form) - shown first on mobile */}
        <div className="w-full md:w-1/2 flex items-center justify-center p-6 md:p-10 z-20">
          <div className="w-full max-w-md">
            {isMobileMode ? (
              <img
                src={appAsset.logo}
                className="w-full md:w-[300px]"
                alt={`${appFeature.company}`}
              />
            ) : null}
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
              Create Account
            </h2>
            <FormProvider {...formMethod}>
              <section className="grid grid-cols-1 gap-2 mb-4">
                <FormRegistration />
              </section>
            </FormProvider>
            <div>
              <p className="text-sm text-gray-500 mt-4 mb-2">
                <div className="flex align-items-center">
                  <Checkbox
                    checked={consent.termsAndConditions}
                    onChange={(e) =>
                      setConsent({
                        ...consent,
                        termsAndConditions: e.checked || false,
                      })
                    }
                  />
                  <p className="ml-2">
                    I agree with the{" "}
                    <a
                      onClick={() => setTermsDialog(true)}
                      className="text-blue-500 hover:underline hover:cursor-pointer"
                    >
                      Terms and Conditions
                    </a>
                  </p>
                </div>
              </p>
              <p className="text-sm text-gray-500 mt-4 mb-2">
                <div className="flex align-items-center">
                  <Checkbox
                    checked={consent.privacyPolicy}
                    onChange={(e) =>
                      setConsent({
                        ...consent,
                        privacyPolicy: e.checked || false,
                      })
                    }
                  />
                  <p className="ml-2">
                    I agree with the{" "}
                    <a
                      onClick={() => setPrivacyConsentDialog(true)}
                      className="text-blue-500 hover:underline hover:cursor-pointer"
                    >
                      Privacy Policy
                    </a>
                  </p>
                </div>
              </p>
            </div>
            <div className="flex flex-col justify-between mt-8">
              <p className="text-sm text-gray-500 mt-4 mb-2">
                Already have an account?{" "}
                <a className="text-blue-600" onClick={() => navigate("/login")}>
                  Log in
                </a>
              </p>
              <Button
                label="Register"
                disabled={
                  consent.termsAndConditions === false ||
                  consent.privacyPolicy === false ||
                  isCreating
                }
                onClick={() =>
                  handleSubmit(
                    (formdata) => handleValidate(formdata),
                    handleValidateError,
                  )()
                }
              ></Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewRegistration;
